import type { TableRow } from "@general-backoffice/core";
import { FormInput, Loading, Modal, notify, TableCard, TableOutput } from "@general-backoffice/core";
import type { PaginationMeta, Refund, User } from "@investhinks/api-client";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Col, FormGroup, Row } from "reactstrap";

import { refundsEndpoints } from "../../api";
import { notifyLoadingToError, notifyLoadingToSuccess } from "../../utils/notify/notifyLoadingUpdate";
import { TypedTable } from "../../utils/typedLib";
import UserSelect from "../users/UserSelect";
import currencySymbol from "../utils/currency";

// Props interfaces
interface RefundFilters {
  createdAt?: {
    $gte?: string;
    $lte?: string;
  };
  pm_status?: {
    $eq?: string;
  };
  "pm_user.id"?: {
    $eq?: number | string;
  };
  [key: string]: unknown;
}

interface RefundsTableCardProps {
  title?: string;
  refunds: Refund[];
  isLoading: boolean;
  pagination: PaginationMeta;
  goToPage: (page: number) => void;
  changePageSize: (size: number) => void;
  filters: RefundFilters;
  setFilters: (filters: RefundFilters) => void;
  columnsToShow?: string[];
  filtersToShow?: string[];
  compact?: boolean;
}

interface FilterModalProps {
  isOpen: boolean;
  toggle: () => void;
  filters: RefundFilters;
  setFilters: (filters: RefundFilters) => void;
  isLoading: boolean;
  filtersToShow: string[];
}

type ExtendedRefund = Refund & {
  isSelected?: boolean;
  pm_user?: { name?: string; email?: string };
  pm_amount?: number;
  pm_currency?: string;
  pm_status?: string;
  pm_invoiceNumber?: string | number;
  createdAt?: string;
};

const statusColor: Record<string, string> = {
  pending: "gray",
  processing: "info",
  completed: "success",
  failed: "danger",
  cancelled: "danger",
};

const RefundsTableCard: React.FC<RefundsTableCardProps> = ({
  title,
  refunds,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  filters,
  setFilters,
  columnsToShow = [],
  filtersToShow = [],
  compact = false,
}) => {
  const { t } = useTranslation("charges", { keyPrefix: "refundsTableCard" });
  const [selectedRefundsId, setSelectedRefundsId] = useState<(number | string)[]>([]);
  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);

  const selectedRefunds = (refunds || []).filter(({ id }) => selectedRefundsId.includes(id));
  const refundsWithIsSelected = (refunds || []).map((ch) =>
    selectedRefundsId.includes(ch.id) ? { ...ch, isSelected: true } : ch
  );
  const existsSelectedRefunds = selectedRefunds.length > 0;

  const onTableChange = (rows: TableRow[] = [], trigger: string) => {
    if (trigger === "selectable") {
      const selectedRefunds = rows.filter((ch) => ch.isSelected).map((ch) => ch.id);
      setSelectedRefundsId(selectedRefunds);
    }
  };

  const downloadSelectedRefunds = async () => {
    const loadingNotification = notify.loading(t("downloadAsPdfs.downloading", "Descargando..."));
    try {
      const blob = await refundsEndpoints.downloadInvoices(selectedRefundsId.map(Number).filter((n) => !isNaN(n)));
      saveAs(blob, "refund_invoices.zip");
      notifyLoadingToSuccess(loadingNotification, t("downloadAsPdfs.downloaded", "Descargado"));
    } catch {
      notifyLoadingToError(loadingNotification, t("downloadAsPdfs.downloadError", "Error al descargar"));
    }
  };

  const downloadSingleInvoice = async (refund: Refund) => {
    const loadingNotification = notify.loading(t("downloadAsPdfs.downloading", "Descargando..."));
    try {
      const blob = await refundsEndpoints.downloadInvoice(refund.id);
      saveAs(blob, `refund_${refund.pm_invoiceNumber}.pdf`);
      notifyLoadingToSuccess(loadingNotification, t("downloadAsPdfs.downloaded", "Descargado"));
    } catch {
      notifyLoadingToError(loadingNotification, t("downloadAsPdfs.downloadError", "Error al descargar"));
    }
  };

  const refundIdsKey = (refunds || []).map(({ id }) => id).join(",");
  useEffect(() => {
    // Keep whatever is still present after a refresh rather than dropping the
    // whole selection.
    const availableIds = new Set((refunds || []).map(({ id }) => id));
    setSelectedRefundsId((prev) => prev.filter((id) => availableIds.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refundIdsKey]);

  const columns = [
    {
      name: t("columns.user"),
      hidden: !columnsToShow.includes("user"),
      formattedContent: ({ pm_user }: ExtendedRefund) => (
        <>
          <div>{pm_user?.name || pm_user?.email}</div>
          {pm_user?.name && <small>{pm_user?.email}</small>}
        </>
      ),
      isTh: true,
    },
    {
      name: t("columns.date"),
      formattedContent: ({ createdAt }: ExtendedRefund) => (
        <span style={compact ? { fontSize: "0.85rem" } : {}}>
          {moment(createdAt).format(compact ? "DD/MM/YY" : "DD/MM/YYYY")}
        </span>
      ),
      isTh: true,
    },
    {
      name: t("columns.state"),
      hidden: compact,
      formattedContent: ({ pm_status }: ExtendedRefund) => {
        const color = statusColor[pm_status || "pending"];
        const validColor =
          color === "success" || color === "danger" || color === "warning" || color === "info" ? color : undefined;
        return <TableOutput.Status status={t(`statuses.${pm_status}`, pm_status || "")} color={validColor} />;
      },
      isTh: true,
    },
    {
      name: t("columns.refund"),
      formattedContent: ({ pm_amount, pm_currency, pm_status }: ExtendedRefund) => (
        <span
          style={compact ? { fontSize: "0.85rem" } : {}}
          className={pm_status === "completed" ? "text-success" : ""}
        >
          {(pm_amount || 0).toFixed(2)} {pm_currency ? currencySymbol[pm_currency] : ""}
          {compact && (
            <small className={`ml-1 ${pm_status === "completed" ? "text-success" : "text-muted"}`}>
              {pm_status === "completed" ? "✓" : ""}
            </small>
          )}
        </span>
      ),
    },
    {
      name: "",
      hidden: compact,
      formattedContent: (refund: ExtendedRefund) => (
        <Button
          size="sm"
          color="primary"
          outline
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            downloadSingleInvoice(refund);
          }}
          title={t("downloadInvoice", "Descargar factura")}
        >
          <i className="fas fa-download" />
        </Button>
      ),
    },
  ];

  return (
    <>
      <FilterModal
        isOpen={isOpenFilters}
        toggle={() => setIsOpenFilters(false)}
        filters={filters}
        setFilters={setFilters}
        isLoading={isLoading}
        filtersToShow={filtersToShow}
      />

      <Card>
        <TableCard.Header title={title || t("title")} subtitle={compact ? "" : t("subtitle")} isLoading={isLoading}>
          {!compact && (
            <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
              <Button
                color={existsSelectedRefunds ? "default" : "secondary"}
                disabled={!existsSelectedRefunds}
                onClick={downloadSelectedRefunds}
              >
                <i className="fas fa-file-invoice mr-1" /> {t("downloadButton")}
              </Button>
              <Button onClick={() => setIsOpenFilters(true)}>
                <i className="fas fa-filter" /> {t("filtersButton")}
              </Button>
            </div>
          )}
        </TableCard.Header>
        {isLoading && <Loading.Line />}

        <TypedTable
          selectable={!compact}
          sortable={false}
          columns={columns}
          rows={refundsWithIsSelected}
          onDataChange={onTableChange}
          isLoading={isLoading}
          noContentMessage={t("noContent")}
          rowHeight={compact ? 50 : 70}
          rowAmount={pagination?.pageSize}
        />
        <TableCard.Footer
          amount={pagination?.pageSize}
          setAmount={changePageSize}
          totalEntries={pagination?.total}
          page={pagination?.page - 1}
          onChangePage={(page) => goToPage(page + 1)}
          isLoading={isLoading}
          amountOptions={compact ? [5, 10] : [5, 10, 20, 50, 500]}
        />
      </Card>
    </>
  );
};

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, toggle, filters, setFilters, isLoading, filtersToShow }) => {
  const { t } = useTranslation("charges", { keyPrefix: "filterModal" });
  const [localFilters, setLocalFilters] = useState<RefundFilters>(filters);
  // Held as an object for display; only the id reaches the query.
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    // Sync on open as well as on change: closing without applying must not
    // leave the form showing something other than the filters in force.
    if (!isOpen) return;
    setLocalFilters(filters);
    if (!filters?.pm_user) setSelectedUser(null);
  }, [isOpen, filters]);

  // "Clean" reads as a full reset, so it clears every filter rather than only
  // the dates while leaving user/status quietly applied.
  const resetFilters = () => {
    setLocalFilters({});
    setSelectedUser(null);
  };

  const changeNestedFilter = (field: string, operator: string, value: string | undefined) => {
    setLocalFilters((f: RefundFilters) => {
      const newFilters: RefundFilters = { ...f };
      if (value === undefined) {
        const fieldValue = newFilters[field];
        if (typeof fieldValue === "object" && fieldValue !== null) {
          // Copy the nested object: deleting in place mutated the parent's
          // filters object directly.
          const fieldObj: Record<string, unknown> = { ...(fieldValue as Record<string, unknown>) };
          delete fieldObj[operator];
          if (Object.keys(fieldObj).length === 0) {
            delete newFilters[field];
          } else {
            newFilters[field] = fieldObj;
          }
        }
      } else {
        const raw = newFilters[field];
        const existing: Record<string, unknown> = typeof raw === "object" && raw !== null ? { ...raw } : {};
        newFilters[field] = { ...existing, [operator]: value };
      }
      return newFilters;
    });
  };

  const changeFilter = (key: string, value: unknown) => {
    setLocalFilters((f: RefundFilters) => ({ ...f, [key]: value }));
  };

  const from = localFilters?.createdAt?.$gte;
  const to = localFilters?.createdAt?.$lte;

  return (
    <Modal.FormContainer
      buttonSave={{
        children: t("applyButton"),
      }}
      closeText={t("closeButton")}
      onSubmit={() => {
        setFilters(localFilters);
        toggle();
      }}
      isOpen={isOpen}
      title={t("title")}
      toggleOpen={toggle}
    >
      <div className="d-flex justify-content-end mb-2 mt--4">
        <Button color="warning" onClick={resetFilters} size="sm">
          <i className="fas fa-broom" /> {t("cleanButton")}
        </Button>
      </div>
      <FormInput.DatePicker
        label={t("refundedAfter")}
        key={from}
        value={from ? moment(from) : undefined}
        onChange={(d: moment.Moment | string) => {
          const newFrom = d && typeof d !== "string" ? d.startOf("day").toISOString() : undefined;
          changeNestedFilter("createdAt", "$gte", newFrom);
        }}
        timeFormat={false}
        dateFormat="DD-MM-YYYY"
        isValidDate={to ? (d: moment.Moment) => d.isSameOrBefore(to) : undefined}
        disabled={isLoading}
      />

      <FormInput.DatePicker
        label={t("refundedBefore")}
        key={to}
        value={to ? moment(to) : undefined}
        onChange={(d: moment.Moment | string) => {
          const newTo = d && typeof d !== "string" ? d.endOf("day").toISOString() : undefined;
          changeNestedFilter("createdAt", "$lte", newTo);
        }}
        timeFormat={false}
        dateFormat="DD-MM-YYYY"
        isValidDate={from ? (d: moment.Moment) => d.isSameOrAfter(from) : undefined}
        disabled={isLoading}
      />

      {filtersToShow.includes("user") && (
        <Row>
          <Col>
            <FormGroup className="mb--4">
              <UserSelect
                label={t("user")}
                user={selectedUser}
                setUser={(user) => {
                  const picked = user && !Array.isArray(user) ? user : null;
                  setSelectedUser(picked);
                  changeFilter("pm_user", picked?.id);
                }}
                isDisabled={isLoading}
                isMulti={false}
                role={undefined}
              />
            </FormGroup>
          </Col>
        </Row>
      )}
    </Modal.FormContainer>
  );
};

export default RefundsTableCard;
